import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaSun, FaMoon } from 'react-icons/fa';
import { useTheme } from "../context/ThemeContext";
import logo from "../assets/logoImage.webp";
import logo2x from "../assets/logoImage@2x.webp";
import { motion } from "framer-motion";

function Header() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  // Track hovered path for the sliding pill animation
  const [hoveredPath, setHoveredPath] = useState(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Desktop nav link
  const NavLink = ({ to, children }) => {
    const active = isActive(to);
    const isHovered = hoveredPath === to;
    const isHighlighted = hoveredPath ? isHovered : active;

    return (
      <Link
        to={to}
        onMouseEnter={() => setHoveredPath(to)}
        className={`
          relative z-10 px-4 py-1.5 rounded-full text-sm font-medium
          transition-colors duration-200 whitespace-nowrap select-none
          ${active || isHovered
            ? 'text-[#FAA617] font-bold'
            : 'text-gray-700 dark:text-gray-300 hover:text-[#FAA617] dark:hover:text-[#FAA617]'
          }
        `}
      >
        {children}
        {/* Active/Hover pill — slides seamlessly between links */}
        {isHighlighted && (
          <motion.span
            layoutId="nav-pill"
            className="absolute inset-0 rounded-full -z-10"
            style={{
              background: 'rgba(250,166,23,0.12)',
              border: '1.5px solid rgba(250,166,23,0.55)',
              boxShadow: '0 0 10px rgba(250,166,23,0.35), inset 0 0 8px rgba(250,166,23,0.08)',
            }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          />
        )}
      </Link>
    );
  };

  // Mobile nav link
  const MobileNavLink = ({ to, children, onClick }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className={`relative flex items-center justify-center w-full py-3 px-4 rounded-xl transition-all duration-300 overflow-hidden ${
          active
            ? 'font-bold text-[#FAA617]'
            : 'text-gray-800 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#38BDF8]/10 hover:text-[#38BDF8] dark:hover:text-[#38BDF8]'
        }`}
        onClick={onClick}
      >
        {active && (
          <div 
            className="absolute inset-0 pointer-events-none rounded-xl"
            style={{
              background: 'rgba(250,166,23,0.1)',
              border: '1px solid rgba(250,166,23,0.3)',
              boxShadow: 'inset 0 0 10px rgba(250,166,23,0.1)',
            }}
          />
        )}
        <span className="relative z-10 flex items-center gap-2">
          {active && <span className="w-1.5 h-1.5 rounded-full bg-[#FAA617] shadow-[0_0_6px_#FAA617]" />}
          {children}
        </span>
      </Link>
    );
  };

  return (
    <header className="w-full xl:w-[90%] max-w-[1300px] sm:w-[85%] w-[95%] mx-auto sticky top-4 z-50 bg-white/90 dark:bg-dark-main/90 backdrop-blur-md rounded-2xl shadow-sm dark:shadow-none transition-all duration-300 mt-4 border border-gray-100 dark:border-gray-800">

      {/* Desktop / Tablet */}
      <div className="hidden md:flex w-full h-20 items-center justify-between px-6">
        {/* Logo */}
        <div className="w-1/2 flex items-center justify-start">
          <Link to="/" className="ml-4 rounded-sm hover:scale-105 transition-transform duration-300">
            <img src={logo} srcSet={`${logo} 1x, ${logo2x} 2x`} alt="logo" width="300" height="200" decoding="async" fetchPriority="high" className="h-15 w-auto" />
          </Link>
        </div>

        {/* Nav + buttons */}
        <div className="flex items-center justify-end gap-8">
          {/* Nav with magic hover pill */}
          <nav
            className="relative flex items-center gap-1"
            onMouseLeave={() => setHoveredPath(null)}
          >
            <NavLink to="/">{t('header.home')}</NavLink>
            <NavLink to="/services">{t('header.services')}</NavLink>
            <NavLink to="/projects">{i18n.language === 'ar' ? 'مشاريعنا' : 'Projects'}</NavLink>
            <NavLink to="/about">{t('header.about')}</NavLink>
            <NavLink to="/careers">{t('header.careers')}</NavLink>
            <NavLink to="/contact">{t('header.contact')}</NavLink>
          </nav>

          {/* Theme + Language buttons */}
          <div className="flex items-center gap-3">
            {/* <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-yellow-primary hover:bg-gray-200 dark:hover:bg-gray-800 transition-all duration-300"
              aria-label="Toggle Theme"
            >
              {isDarkMode ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
            </button> */}
            <button
              onClick={toggleLanguage}
              className="h-8 w-8 rounded-full bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/30 font-bold text-sm flex items-center justify-center hover:bg-[#38BDF8]/20 transition-all duration-300 hover:scale-110 shadow-[0_0_10px_rgba(56,189,248,0.15)]"
            >
              {t('header.language')}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden w-full bg-transparent">
        <div className="flex items-center justify-between px-4 py-3 relative z-50">
          <div className="rounded flex items-center justify-center hover:scale-105 transition-transform duration-300">
            <Link to="/"><img src={logo} srcSet={`${logo} 1x, ${logo2x} 2x`} alt="logo" width="300" height="200" decoding="async" fetchPriority="high" className="h-10 w-auto drop-shadow-[0_0_8px_rgba(56,189,248,0.3)]" /></Link>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="h-8 w-8 rounded-full bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/30 font-bold text-xs flex items-center justify-center hover:bg-[#38BDF8]/20 transition-all duration-300 hover:scale-110 shadow-[0_0_10px_rgba(56,189,248,0.1)]"
            >
              {t('header.language')}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-xl transition-all duration-300 flex items-center justify-center ${isMenuOpen ? 'bg-[#FAA617]/10 text-[#FAA617] border border-[#FAA617]/30 shadow-[0_0_10px_rgba(250,166,23,0.15)]' : 'bg-gray-100 dark:bg-[#0B1120] text-gray-900 dark:text-[#38BDF8] border border-transparent dark:border-[#38BDF8]/20 hover:bg-gray-200 dark:hover:bg-[#38BDF8]/10'}`}
              aria-label="menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Backdrop */}
        <div 
          className={`fixed inset-0 z-40 bg-[#040d1f]/40 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} 
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Mobile Dropdown */}
        <div 
          className={`absolute top-[calc(100%+0.5rem)] left-0 right-0 z-50 p-4 bg-white/95 dark:bg-dark-main/95 backdrop-blur-xl border border-gray-100 dark:border-[#38BDF8]/20 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] transition-all duration-300 origin-top ${isMenuOpen ? 'opacity-100 scale-y-100 translate-y-0' : 'opacity-0 scale-y-95 -translate-y-2 pointer-events-none'}`}
        >
          <nav className="flex flex-col gap-3">
            <MobileNavLink to="/" onClick={() => setIsMenuOpen(false)}>{t('header.home')}</MobileNavLink>
            <MobileNavLink to="/services" onClick={() => setIsMenuOpen(false)}>{t('header.services')}</MobileNavLink>
            <MobileNavLink to="/projects" onClick={() => setIsMenuOpen(false)}>{i18n.language === 'ar' ? 'مشاريعنا' : 'Projects'}</MobileNavLink>
            <MobileNavLink to="/about" onClick={() => setIsMenuOpen(false)}>{t('header.about')}</MobileNavLink>
            <MobileNavLink to="/careers" onClick={() => setIsMenuOpen(false)}>{t('header.careers')}</MobileNavLink>
            <MobileNavLink to="/contact" onClick={() => setIsMenuOpen(false)}>{t('header.contact')}</MobileNavLink>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default Header;